/**
 * The chart of accounts (piece 23; decisions 0100, 0195, 0217, 0221, 0239):
 * the specification's 42 base accounts, every number transcribed digit for
 * digit from its Base Chart of Accounts sections, and the seven the plan
 * added for the routes the posting map names — 2050, 2150, 2250, 5300, 6950
 * (2026-09-06), 4500 (2026-09-07) and 2210 (2026-09-08). Spelled once here:
 * the migration inserts these rows, the reset restores them, the piece-rows
 * seed writes any a database lacks, and the transcription check reads them.
 */
import { t } from '../strings';

export const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'] as const;
export type AccountTypeValue = (typeof ACCOUNT_TYPES)[number];

/** The group order the Chart of Accounts list uses: Assets, Liabilities, Equity, Revenue, Expense. */
export const ACCOUNT_TYPE_ORDER: readonly AccountTypeValue[] = ACCOUNT_TYPES;
export const ACCOUNT_TYPE_LABELS: Readonly<Record<AccountTypeValue, string>> = { Asset: 'Assets', Liability: 'Liabilities', Equity: 'Equity', Revenue: 'Revenue', Expense: 'Expense' };

export const ACCOUNT_DETAIL_TYPES = [
  'CurrentAsset', 'FixedAsset', 'OtherAsset', 'CurrentLiability', 'LongTermLiability', 'EquityDetail', 'Income', 'CostOfGoodsSold', 'OperatingExpense', 'OtherIncome', 'OtherExpense',
] as const;
export type AccountDetailTypeValue = (typeof ACCOUNT_DETAIL_TYPES)[number];

/**
 * What each subtype reads as on a screen, the way `ACCOUNT_TYPE_LABELS` names
 * the five types. 🔨 A picker offers the words and stores the member; it never
 * draws `CostOfGoodsSold` at a person (found in review, 2026-09-15).
 */
export const ACCOUNT_DETAIL_TYPE_LABELS: Readonly<Record<AccountDetailTypeValue, string>> = {
  CurrentAsset: t('accounting.detailType.currentAsset'),
  FixedAsset: t('accounting.detailType.fixedAsset'),
  OtherAsset: t('accounting.detailType.otherAsset'),
  CurrentLiability: t('accounting.detailType.currentLiability'),
  LongTermLiability: t('accounting.detailType.longTermLiability'),
  EquityDetail: t('accounting.detailType.equityDetail'),
  Income: t('accounting.detailType.income'),
  CostOfGoodsSold: t('accounting.detailType.costOfGoodsSold'),
  OperatingExpense: t('accounting.detailType.operatingExpense'),
  OtherIncome: t('accounting.detailType.otherIncome'),
  OtherExpense: t('accounting.detailType.otherExpense'),
};

/** The subtypes each type admits; the balance sheet groups by them (decision 0221). */
export const DETAIL_TYPES_BY_TYPE: Readonly<Record<AccountTypeValue, readonly AccountDetailTypeValue[]>> = {
  Asset: ['CurrentAsset', 'FixedAsset', 'OtherAsset'],
  Liability: ['CurrentLiability', 'LongTermLiability'],
  Equity: ['EquityDetail'],
  Revenue: ['Income', 'OtherIncome'],
  Expense: ['CostOfGoodsSold', 'OperatingExpense', 'OtherExpense'],
};

export const CASH_FLOW_SECTIONS = ['Operating', 'Investing', 'Financing', 'Excluded'] as const;
export type CashFlowSectionValue = (typeof CASH_FLOW_SECTIONS)[number];

export interface ChartAccount {
  accountNumber: string;
  name: string;
  type: AccountTypeValue;
  detailType: AccountDetailTypeValue;
  cashFlowSection: CashFlowSectionValue;
  description: string;
  isContra?: boolean;
  isControlAccount?: boolean;
}

const asset = (accountNumber: string, name: string, detailType: AccountDetailTypeValue, cashFlowSection: CashFlowSectionValue, description: string, flags: Partial<ChartAccount> = {}): ChartAccount =>
  ({ accountNumber, name, type: 'Asset', detailType, cashFlowSection, description, ...flags });
const liability = (accountNumber: string, name: string, detailType: AccountDetailTypeValue, cashFlowSection: CashFlowSectionValue, description: string, flags: Partial<ChartAccount> = {}): ChartAccount =>
  ({ accountNumber, name, type: 'Liability', detailType, cashFlowSection, description, ...flags });
const equity = (accountNumber: string, name: string, cashFlowSection: CashFlowSectionValue, description: string, flags: Partial<ChartAccount> = {}): ChartAccount =>
  ({ accountNumber, name, type: 'Equity', detailType: 'EquityDetail', cashFlowSection, description, ...flags });
const revenue = (accountNumber: string, name: string, detailType: AccountDetailTypeValue, description: string): ChartAccount =>
  ({ accountNumber, name, type: 'Revenue', detailType, cashFlowSection: 'Operating', description });
const cogs = (accountNumber: string, name: string, description: string): ChartAccount =>
  ({ accountNumber, name, type: 'Expense', detailType: 'CostOfGoodsSold', cashFlowSection: 'Operating', description });
const expense = (accountNumber: string, name: string, description: string): ChartAccount =>
  ({ accountNumber, name, type: 'Expense', detailType: 'OperatingExpense', cashFlowSection: 'Operating', description });

/** The 49 seeded accounts, in number order. Cash and retained earnings are Excluded from the cash flow sections: the first is the statement's total, the second is computed. */
export const CHART_OF_ACCOUNTS: readonly ChartAccount[] = [
  asset('1000', 'Cash — Operating', 'CurrentAsset', 'Excluded', 'Primary business checking account.'),
  asset('1010', 'Cash — Payroll', 'CurrentAsset', 'Excluded', 'Dedicated payroll disbursement account.'),
  asset('1100', 'Accounts Receivable', 'CurrentAsset', 'Operating', 'Amounts owed by customers; a Company’s receivable account points here by default.', { isControlAccount: true }),
  asset('1200', 'Inventory', 'CurrentAsset', 'Operating', 'Value of Items on hand.'),
  asset('1300', 'Prepaid Expenses', 'CurrentAsset', 'Operating', 'Insurance, subscriptions, and other costs paid in advance; a vendor prepayment rests here.'),
  asset('1500', 'Fixed Assets — Vehicles', 'FixedAsset', 'Investing', 'Company vehicles at cost.'),
  asset('1510', 'Fixed Assets — Shop Equipment', 'FixedAsset', 'Investing', 'Tools and shop equipment at cost.'),
  asset('1590', 'Accumulated Depreciation', 'FixedAsset', 'Operating', 'Contra-asset; offsets Fixed Assets.', { isContra: true }),
  liability('2000', 'Accounts Payable', 'CurrentLiability', 'Operating', 'Amounts owed to vendors.', { isControlAccount: true }),
  liability('2050', 'Goods Received Not Invoiced', 'CurrentLiability', 'Operating', 'A clearing account, never a resting place: a receipt before its bill credits it and the bill clears it (decision 0195).'),
  liability('2100', 'Sales Tax Payable', 'CurrentLiability', 'Operating', 'Sales tax collected, pending remittance; one sub-account per state registration.'),
  liability('2150', 'Backup Withholding Payable', 'CurrentLiability', 'Operating', 'Backup withholding withheld from a vendor payment until it is remitted.'),
  liability('2200', 'Accrued Payroll', 'CurrentLiability', 'Operating', 'Wages earned but not yet paid.'),
  liability('2210', 'Commissions Payable', 'CurrentLiability', 'Operating', 'Commission accrued when an invoice is sent, cleared by the payroll journal that pays it (decision 0217).'),
  liability('2250', 'Employee Reimbursements Payable', 'CurrentLiability', 'Operating', 'Approved reimbursable expenses not yet paid to the employee (decision 0199).'),
  liability('2300', 'Customer Deposits', 'CurrentLiability', 'Operating', 'Prepayments held before goods or services are delivered.'),
  liability('2400', 'Credit Card Payable', 'CurrentLiability', 'Operating', 'Outstanding balance on company credit cards.'),
  liability('2500', 'Notes Payable', 'LongTermLiability', 'Financing', 'Business loans and financed equipment.'),
  equity('3000', 'Owner’s Equity', 'Financing', 'Owner capital contributions.'),
  equity('3100', 'Retained Earnings', 'Excluded', 'Accumulated prior-year net income; three writers only, the go-live import, Set Opening Balance on an account’s page and a Manual Journal with a reason (decisions 0111, 10120).'),
  equity('3900', 'Owner’s Draws', 'Financing', 'Contra-equity; owner withdrawals.', { isContra: true }),
  revenue('4000', 'Product Sales', 'Income', 'Revenue from parts and equipment; the default Invoice line account for a Good.'),
  revenue('4100', 'Service Revenue', 'Income', 'Revenue from labor and service Work Orders; the default for a Service.'),
  revenue('4200', 'Shipping Revenue', 'Income', 'Shipping charges billed to customers.'),
  revenue('4300', 'Late Fee Income', 'OtherIncome', 'A flat penalty charged once; posted separately so it is never mistaken for product or service revenue.'),
  revenue('4400', 'Finance Charge Income', 'OtherIncome', 'Interest or finance charges billed to customers; the repeating percentage of an unpaid balance (decision 0163).'),
  revenue('4500', 'Card Surcharge Income', 'OtherIncome', 'The card surcharge a customer pays for choosing a card; a recovered cost, never sales (decision 0239).'),
  cogs('5000', 'Cost of Goods Sold — Parts', 'Cost of parts and equipment sold.'),
  cogs('5100', 'Cost of Labor — Billable', 'Direct labor cost for billable Work Order time.'),
  cogs('5200', 'Freight & Landed Cost', 'Inbound freight and customs added to Bill landed cost.'),
  cogs('5300', 'Inventory Adjustment', 'The costed difference a stock count or a revaluation posts against 1200 (decision 0195).'),
  expense('6000', 'Payroll Expense — Non-Billable', 'Wages for non-billable time and salaried staff.'),
  expense('6100', 'Payroll Tax Expense', 'Employer-side payroll taxes.'),
  expense('6200', 'Rent Expense', 'Shop and office rent.'),
  expense('6300', 'Utilities Expense', 'Electric, gas, water, internet.'),
  expense('6400', 'Insurance Expense', 'General liability, vehicle, and workers’ comp insurance.'),
  expense('6500', 'Vehicle Expense', 'Fuel, maintenance, and vehicle-related costs not tied to a Work Order.'),
  expense('6600', 'Travel Expense', 'Matches the Travel and Lodging Expense Categories, so a logged Expense posts here automatically.'),
  expense('6610', 'Meals Expense', 'Matches its own Expense Category.'),
  expense('6620', 'Subcontractor Expense', 'Subcontracted work; the default purchase account for a Service.'),
  expense('6630', 'Equipment Rental Expense', 'Rented equipment and tools.'),
  expense('6640', 'Mileage Reimbursement', 'Matches its own Expense Category.'),
  expense('6700', 'Depreciation Expense', 'Periodic depreciation posted via Manual Journal.'),
  expense('6800', 'Bank Fees', 'Account fees and service charges.'),
  expense('6810', 'Credit Card Processing Fees', 'Card-processing fees passed through as a surcharge or absorbed on other channels.'),
  expense('6900', 'Sales Commission Expense', 'Commission payouts, accrued when the invoice is sent (decision 0217).'),
  expense('6910', 'Office Supplies Expense', 'General office and administrative supplies; the Supplies Expense Category posts here.'),
  expense('6920', 'Professional Fees', 'Accounting, legal, and consulting fees.'),
  expense('6950', 'Bad Debt Expense', 'An invoice written off as uncollectible (decision 0197).'),
];

/** The 42 numbers the specification's own sections carry, for the transcription check. */
export const SPECIFICATION_ACCOUNT_NUMBERS: readonly string[] = [
  '1000', '1010', '1100', '1200', '1300', '1500', '1510', '1590',
  '2000', '2100', '2200', '2300', '2400', '2500',
  '3000', '3100', '3900',
  '4000', '4100', '4200', '4300', '4400',
  '5000', '5100', '5200',
  '6000', '6100', '6200', '6300', '6400', '6500', '6600', '6610', '6620', '6630', '6640', '6700', '6800', '6810', '6900', '6910', '6920',
];

/** The seven the plan added beside the specification's 42, each for a route the posting map names. */
export const PLAN_ADDED_ACCOUNT_NUMBERS: readonly string[] = ['2050', '2150', '2250', '5300', '6950', '4500', '2210'];

export function chartAccount(accountNumber: string): ChartAccount {
  const row = CHART_OF_ACCOUNTS.find((a) => a.accountNumber === accountNumber);
  if (!row) throw new Error(`${accountNumber} is not in the seeded chart of accounts`);
  return row;
}

/** The type defaults of decision 0210: a Good sells through 4000 and buys into 1200; a Service sells through 4100 and buys through 6620. */
export const ITEM_TYPE_ACCOUNT_DEFAULTS: Readonly<Record<'Good' | 'Service', { sales: string; purchase: string }>> = {
  Good: { sales: '4000', purchase: '1200' },
  Service: { sales: '4100', purchase: '6620' },
};

export interface AccountInput {
  accountNumber: string;
  name: string;
  type: AccountTypeValue;
  detailType: AccountDetailTypeValue | null | undefined;
  cashFlowSection: CashFlowSectionValue | null | undefined;
}

/** 🔨 An account saved with no subtype, or with no section, or with a subtype its type does not admit, is refused — the intended friction of decision 0221. */
export function accountProblem(input: AccountInput): string | null {
  if (!/^\d{4}$/.test(input.accountNumber.trim())) return 'an account number is four digits';
  if (!input.name.trim()) return 'an account carries a name; type the name this account is known by';
  if (!(ACCOUNT_TYPES as readonly string[]).includes(input.type)) return 'an account type is Asset, Liability, Equity, Revenue or Expense';
  if (!input.detailType) return 'an account carries a subtype; the balance sheet groups by it';
  if (!DETAIL_TYPES_BY_TYPE[input.type].includes(input.detailType)) return `${input.detailType} is not a subtype of ${input.type}`;
  if (!input.cashFlowSection) return 'an account carries a cash flow section: Operating, Investing, Financing or Excluded';
  if (!(CASH_FLOW_SECTIONS as readonly string[]).includes(input.cashFlowSection)) return 'a cash flow section is Operating, Investing, Financing or Excluded';
  return null;
}

/** Which way an account's normal balance runs: a debit balance is positive for assets and expenses, a credit balance positive for the rest; a contra account runs the other way. */
export function normalBalanceSign(type: AccountTypeValue, isContra = false): 1 | -1 {
  const debitNormal = type === 'Asset' || type === 'Expense';
  return (debitNormal !== isContra ? 1 : -1) as 1 | -1;
}

/** A balance the way the statement presents it: a contra account shows its own sign, never as a negative of its parent. */
export function presentedBalance(signedSum: number, type: AccountTypeValue, isContra = false): number {
  const presented = signedSum * normalBalanceSign(type, isContra);
  // A zero balance is zero, never the negative zero a credit-normal account's sign would make of it.
  return presented === 0 ? 0 : presented;
}

/**
 * 🔒 How an account is named on any screen (decision 0478): its number, then
 * its name. Never its id.
 *
 * 🔨 The number comes first because that is the order an accountant reads and
 * searches in, and because two accounts are allowed to share a name while no
 * two share a number. 💰 The Item page drew `item.salesAccountId` and
 * `item.purchaseAccountId` raw until 2026-09-17, so both rows read as a uuid
 * — the same defect decision 0472 named for job labels, one level down.
 */
export function accountText(account: { accountNumber: string; name: string } | null | undefined): string | null {
  return account ? `${account.accountNumber} ${account.name}` : null;
}
